import { useState, useEffect } from 'react';
import { billingAPI, adminAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  CreditCard,
  Loader2,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Clock,
  CheckCircle2,
  MessageCircle,
  Phone,
  Send,
  ExternalLink,
  Globe,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';

// Removed credit packages


export default function Billing() {
  const { user, isAdmin, refreshUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [billingSettings, setBillingSettings] = useState(null);
  const [vipConfig, setVipConfig] = useState({ price: 500000, expiry_days: 30 });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // Fetch transactions
        try {
          const txRes = await billingAPI.getTransactions();
          setTransactions(txRes.data);
        } catch (err) {
          console.error('Failed to fetch transactions:', err);
        }

        // Fetch settings (Critical for billing_mode)
        try {
          const settingsRes = await billingAPI.getSettings();
          setBillingSettings(settingsRes.data);
        } catch (err) {
          console.error('Failed to fetch billing settings:', err);
          toast.error('Không thể tải cài đặt thanh toán');
        }

        // Fetch VIP config (Prices)
        try {
          const vipRes = await adminAPI.getVipConfig();
          if (vipRes.data) {
            setVipConfig(vipRes.data);
          }
        } catch (err) {
          console.error('Failed to fetch VIP config:', err);
        }

      } catch (error) {
        console.error('Unexpected error in billing data fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleDeposit = async () => {
    setDepositing(true);
    try {
      // For now, "depositing" means buying the VIP package
      await billingAPI.deposit(vipConfig.price); 
      // Note: The backend deposit endpoint might need to be updated 
      // to handle VIP activation if it doesn't already.
      // But for now, we follow the user's "nạp vip" request.
      await refreshUser();
      const txRes = await billingAPI.getTransactions();
      setTransactions(txRes.data);
      toast.success(`Nâng cấp VIP thành công!`);
      setDepositDialogOpen(false);
    } catch (error) {
      toast.error('Nâng cấp thất bại');
    } finally {
      setDepositing(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const isManualMode = billingSettings?.billing_mode === 'manual';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  const contactLinks = [
    { label: 'Zalo', url: billingSettings?.zalo_link, icon: MessageCircle, color: '#0068FF', bg: '#E6F0FF' },
    { label: 'Facebook', url: billingSettings?.facebook_link, icon: Globe, color: '#1877F2', bg: '#E7F3FF' },
    { label: 'Telegram', url: billingSettings?.telegram_link, icon: Send, color: '#0088CC', bg: '#E3F2FD' },
    { label: 'Hotline', url: billingSettings?.hotline ? `tel:${billingSettings.hotline}` : null, displayText: billingSettings?.hotline, icon: Phone, color: '#31A24C', bg: '#EBF5ED' },
  ].filter(l => l.url || l.displayText);

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="animate-fadeIn">
        <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
          <Crown className="text-amber-500" />
          Nâng cấp VIP
        </h1>
        <p className="text-[#606770] mt-1">
          Kích hoạt gói thành viên để sử dụng đầy đủ tính năng
        </p>

      </div>

      {/* Balance Card */}
      {/* Balance Card - Premium Design */}
      <div className="animate-fadeIn stagger-1 relative overflow-hidden rounded-3xl p-[1px] bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600 shadow-2xl group">
        <div className={`relative h-full w-full rounded-3xl overflow-hidden ${
          user?.subscription_type === 'monthly' 
            ? 'bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]' 
            : 'bg-gradient-to-br from-orange-500 to-orange-600'
        }`}>
          {/* Subtle patterns */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-amber-400 rounded-full blur-[80px] opacity-30 animate-pulse"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-orange-400 rounded-full blur-[80px] opacity-20"></div>
          </div>

          <CardContent className="relative p-5 md:p-8 text-white">
            <div className="flex flex-col h-full min-h-[180px] md:min-h-[220px] justify-between">
              {/* Top: Brand & Type */}
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20 shadow-inner">
                    <Crown size={18} className={user?.subscription_type === 'monthly' ? 'text-amber-400' : 'text-white'} />
                  </div>
                  <div>
                    <h2 className="text-sm md:text-xl font-black tracking-tight uppercase leading-tight">
                      TRITECHZ <span className="font-thin block md:inline">SOLUTIONS</span>
                    </h2>
                    <p className="text-[8px] md:text-[10px] tracking-[0.2em] text-white/50 uppercase">Premium Network</p>
                  </div>
                </div>
                
                <div className="text-right flex-shrink-0">
                  <Badge className={`${
                    user?.subscription_type === 'monthly' 
                      ? 'bg-gradient-to-r from-amber-400 to-amber-600 text-black' 
                      : 'bg-white/20 text-white'
                  } border-none font-black px-2 md:px-4 py-0.5 md:py-1 rounded-full text-[8px] md:text-[10px] tracking-widest shadow-lg whitespace-nowrap`}>
                    {isAdmin ? "ADMIN" : (user?.subscription_type === 'monthly' ? "VIP MEMBER" : "REGULAR")}
                  </Badge>
                </div>
              </div>

              {/* Middle: User Info & Chip */}
              <div className="flex items-center gap-4 md:gap-6 mt-4 md:mt-0">
                {/* Virtual Chip */}
                <div className="relative w-10 h-7 md:w-12 md:h-9 rounded-md bg-gradient-to-br from-amber-200 to-amber-500 overflow-hidden shadow-inner flex-shrink-0">
                  <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-[1px] opacity-30">
                    <div className="border border-black/10"></div><div className="border border-black/10"></div><div className="border border-black/10"></div>
                    <div className="border border-black/10"></div><div className="border border-black/10"></div><div className="border border-black/10"></div>
                  </div>
                </div>
                
                <div className="min-w-0">
                  <p className="text-[8px] md:text-[10px] text-white/40 uppercase tracking-[0.2em]">Chủ sở hữu</p>
                  <h3 className="text-sm md:text-xl font-bold tracking-wide mt-0.5 md:mt-1 truncate">
                    {user?.name?.toUpperCase() || "QUÝ KHÁCH"}
                  </h3>
                </div>
              </div>

              {/* Bottom: Expiry & Action */}
              <div className="flex flex-col md:flex-row items-start md:items-end justify-between mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/10 gap-4">
                <div className="flex-1">
                  {user?.subscription_type === 'monthly' && user?.subscription_expiry ? (
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-[8px] md:text-[10px] text-white/40 uppercase tracking-[0.2em]">Ngày hết hạn</p>
                        <p className="text-md md:text-xl font-black text-amber-400 mt-0.5">
                          {new Date(user.subscription_expiry).toLocaleDateString('vi-VN')}
                        </p>
                      </div>
                      <div className="h-6 md:h-8 w-[1px] bg-white/10 mx-1 md:mx-2"></div>
                      <div>
                        <p className="text-[8px] md:text-[10px] text-white/40 uppercase tracking-[0.2em]">Trạng thái</p>
                        <div className="flex items-center gap-1 mt-0.5 text-green-400 font-bold text-[10px] md:text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                          ACTIVE
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[8px] md:text-[10px] text-white/40 uppercase tracking-[0.2em]">Trạng thái</p>
                      <p className="text-sm md:text-xl font-bold text-white/90 mt-0.5 italic">FREE ACCOUNT</p>
                    </div>
                  )}
                </div>

                <div className="w-full md:w-auto">
                  {user?.subscription_type !== 'monthly' && !isManualMode && (
                    <Button
                      onClick={() => setDepositDialogOpen(true)}
                      className="w-full md:w-auto bg-white text-orange-600 hover:bg-white/90 font-black rounded-lg md:rounded-xl px-4 md:px-6 py-2 md:py-6 h-auto text-xs md:text-base shadow-xl"
                    >
                      <Plus size={16} className="mr-1.5 md:mr-2" />
                      KÍCH HOẠT VIP
                    </Button>
                  )}
                  {user?.subscription_type === 'monthly' && !isManualMode && (
                    <Button
                      onClick={() => setDepositDialogOpen(true)}
                      className="w-full md:w-auto bg-white/10 hover:bg-white/20 text-white backdrop-blur-xl border border-white/20 font-black rounded-lg md:rounded-xl px-4 md:px-6 py-2 md:py-6 h-auto text-xs md:text-base shadow-xl"
                    >
                      <Clock size={16} className="mr-1.5 md:mr-2" />
                      GIA HẠN GÓI
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>

          {/* Hologram shine effect - disabled for pure mobile performance or kept subtle */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-[1.5s] ease-in-out"></div>
        </div>
      </div>

      {/* Manual Mode: Contact Admin */}
      {isManualMode && (
        <Card className="animate-fadeIn stagger-2 border-2 border-amber-500/20" data-testid="manual-deposit-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Liên hệ Admin để kích hoạt VIP</CardTitle>
            <CardDescription>
              Liên hệ qua các kênh bên dưới để được hỗ trợ nâng cấp gói VIP
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {billingSettings?.billing_contact_info && (
              <div className="p-4 bg-[#FEF9E6] border border-[#FFE69C] rounded-xl text-sm text-[#606770] whitespace-pre-wrap" data-testid="billing-contact-info">
                {billingSettings.billing_contact_info}
              </div>
            )}

            {contactLinks.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {contactLinks.map((link) => {
                  const IconComponent = link.icon;
                  return (
                    <a
                      key={link.label}
                      href={link.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 border rounded-xl hover:shadow-md transition-shadow"
                      data-testid={`billing-contact-${link.label.toLowerCase()}`}
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: link.bg, color: link.color }}
                      >
                        <IconComponent size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1C1E21] text-sm">{link.label}</p>
                        <p className="text-xs text-[#606770] truncate">{link.displayText || link.url}</p>
                      </div>
                      <ExternalLink size={14} className="text-[#8C939D] flex-shrink-0" />
                    </a>
                  );
                })}
              </div>
            )}

            {contactLinks.length === 0 && !billingSettings?.billing_contact_info && (
              <div className="text-center py-6 text-[#8C939D]">
                <Phone size={32} className="mx-auto mb-2 opacity-50" />
                <p>Thông tin liên hệ chưa được cấu hình. Vui lòng quay lại sau.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Auto Mode: VIP Package Selection */}
      {!isManualMode && (
        <Card className="animate-fadeIn stagger-2 border-amber-200 bg-amber-50/30" data-testid="packages-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Crown size={20} className="text-amber-500" />
              Gói thành viên VIP
            </CardTitle>
            <CardDescription>Kích hoạt để sử dụng toàn bộ tính năng cao cấp</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center gap-8 p-6 bg-white rounded-2xl border-2 border-amber-200 shadow-sm">
              <div className="text-center md:text-left space-y-2">
                <p className="text-4xl font-black text-amber-600">
                  {formatCurrency(vipConfig.price)}
                </p>
                <p className="text-sm font-bold text-gray-500 uppercase tracking-widest">
                  Thời hạn: {vipConfig.expiry_days} ngày
                </p>
              </div>
              <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-100 pt-6 md:pt-0 md:pl-8">
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={16} className="text-green-500" />
                    Sử dụng không giới hạn các tính năng VIP
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={16} className="text-green-500" />
                    Ưu tiên hỗ trợ từ Admin
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={16} className="text-green-500" />
                    Cập nhật các tính năng mới sớm nhất
                  </li>
                </ul>
              </div>
              <Button 
                onClick={() => setDepositDialogOpen(true)}
                className="w-full md:w-auto h-12 px-8 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold rounded-xl shadow-lg shadow-amber-200/50"
              >
                Gia hạn / Kích hoạt ngay
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Card className="animate-fadeIn stagger-3" data-testid="transactions-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Lịch sử giao dịch</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div 
                  key={tx.id}
                  className="flex items-center justify-between p-4 bg-[#F7F8FA] rounded-xl"
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.type === 'deposit' ? 'bg-[#EBF5ED]' : 'bg-[#FCE9EC]'
                    }`}>
                      {tx.type === 'deposit' ? (
                        <ArrowDownRight size={20} className="text-[#31A24C]" />
                      ) : (
                        <ArrowUpRight size={20} className="text-[#E41E3F]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#1C1E21]">{tx.description}</p>
                      <p className="text-xs text-[#8C939D]">{new Date(tx.created_at).toLocaleString('vi-VN')}</p>
                    </div>
                  </div>
                  <div className={`font-bold ${tx.type === 'deposit' ? 'text-[#31A24C]' : 'text-[#E41E3F]'}`}>
                    {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(tx.amount)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Clock size={48} className="empty-state-icon mx-auto" />
              <h3 className="empty-state-title">Chưa có giao dịch</h3>
              <p className="empty-state-text">Lịch sử giao dịch sẽ hiển thị ở đây</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto Deposit Dialog */}
      {!isManualMode && (
        <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
          <DialogContent data-testid="deposit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown size={20} className="text-amber-500" />
                Xác nhận nâng cấp VIP
              </DialogTitle>
              <DialogDescription>
                Bạn đang thực hiện thanh toán để kích hoạt gói VIP {vipConfig.expiry_days} ngày.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-center">
                <p className="text-sm text-amber-800 font-medium">Số tiền thanh toán</p>
                <p className="text-3xl font-black text-amber-600">{formatCurrency(vipConfig.price)}</p>
                <div className="mt-2 flex items-center justify-center gap-2 text-xs text-amber-700 bg-white/50 py-1 rounded-full">
                  <Clock size={12} />
                  <span>Thời hạn: {vipConfig.expiry_days} ngày</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDepositDialogOpen(false); }} disabled={depositing}>Hủy</Button>
              <Button onClick={handleDeposit} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold" disabled={depositing} data-testid="confirm-deposit-btn">
                {depositing ? <><Loader2 size={16} className="mr-2 animate-spin" />Đang xử lý...</> : <><CheckCircle2 size={16} className="mr-2" />Xác nhận thanh toán</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
